import type { ReactNode, SelectHTMLAttributes } from "react";

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  /** Wrapper sizing. Defaults to inline width; pass "block" for form fields. */
  containerClassName?: string;
  /** Control height: "md" (h-9) for forms, "sm" (h-8) for dense filter rows. */
  size?: "sm" | "md";
  children: ReactNode;
};

const SIZE_CLASSES: Record<"sm" | "md", { select: string; chevron: string }> = {
  sm: { select: "h-8 pl-2.5 pr-7", chevron: "right-2" },
  md: { select: "h-9 pl-3 pr-8", chevron: "right-2.5" },
};

// Shared dropdown style for native <select> form fields. Mirrors the neutral
// FilterDropdown trigger (rounded-md, slate border, ▾ chevron) so every
// dropdown in the app reads the same. Use FilterDropdown for filter/menu
// choices and this component for form selects; do not style raw <select> ad hoc.
export default function Select({
  containerClassName = "inline-block",
  className = "",
  size = "md",
  children,
  ...rest
}: SelectProps) {
  const sizing = SIZE_CLASSES[size];
  return (
    <div className={`relative ${containerClassName}`}>
      <select
        {...rest}
        className={`${sizing.select} w-full appearance-none rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-default disabled:opacity-60 ${className}`}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-slate-400 ${sizing.chevron}`}
      >
        ▾
      </span>
    </div>
  );
}
